import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GetMocktestQuestion } from "../../../Client/request";
import { getQuestionId } from "../utils/listeningPreload";
import {
  getCategoryQuestionsFromStep,
  getNextCategory,
  getPreloadCategory,
  getPreloadWindowQuestions,
  isCurrentQuestionReady,
  shouldMarkPreloadFailed,
} from "../utils/mockQuestionPreload";

export const useMockQuestionPreload = (questions = [], currentStep = 1) => {
  const [questionCache, setQuestionCache] = useState({});
  const [preloadFailures, setPreloadFailures] = useState({});
  const questionCacheRef = useRef({});
  const preloadFailuresRef = useRef({});
  const inFlightRef = useRef({});

  const currentCategory = questions[currentStep - 1]?.Category || null;
  const currentPreloadCategory = getPreloadCategory(currentCategory);
  const nextPreloadCategory = getPreloadCategory(
    getNextCategory(questions, currentCategory)
  );
  const currentWindowQuestions = useMemo(
    () =>
      currentPreloadCategory
        ? getPreloadWindowQuestions(questions, currentStep, currentPreloadCategory)
        : [],
    [currentPreloadCategory, currentStep, questions]
  );

  const setCachedQuestion = useCallback((questionId, questionData) => {
    questionCacheRef.current = {
      ...questionCacheRef.current,
      [questionId]: questionData,
    };
    setQuestionCache(questionCacheRef.current);
  }, []);

  const markQuestionFailed = useCallback((questionId) => {
    preloadFailuresRef.current = {
      ...preloadFailuresRef.current,
      [questionId]: true,
    };
    setPreloadFailures(preloadFailuresRef.current);
  }, []);

  const ensureQuestionData = useCallback(
    async (question) => {
      const questionId = getQuestionId(question);
      if (!questionId) return null;

      if (questionCacheRef.current[questionId]) {
        return questionCacheRef.current[questionId];
      }

      if (!inFlightRef.current[questionId]) {
        inFlightRef.current[questionId] = GetMocktestQuestion(question.QuestionId)
          .then((response) => {
            if (response?.data) {
              setCachedQuestion(questionId, response.data);
              return response.data;
            }
            if (shouldMarkPreloadFailed(response)) {
              markQuestionFailed(questionId);
            }
            return null;
          })
          .catch((error) => {
            console.error("Error preloading mock question:", error);
            markQuestionFailed(questionId);
            return null;
          })
          .finally(() => {
            delete inFlightRef.current[questionId];
          });
      }

      return inFlightRef.current[questionId];
    },
    [markQuestionFailed, setCachedQuestion]
  );

  useEffect(() => {
    let cancelled = false;

    const preloadCategoryWindow = async (category) => {
      if (!category || cancelled) return;
      const categoryQuestions = getPreloadWindowQuestions(questions, currentStep, category);

      if (categoryQuestions[0]) {
        await ensureQuestionData(categoryQuestions[0]);
      }

      await Promise.all(categoryQuestions.slice(1).map(ensureQuestionData));
    };

    const preloadCategoryInBackground = (category) => {
      if (!category || cancelled) return;
      getCategoryQuestionsFromStep(questions, currentStep, category).forEach(
        (question) => {
          if (!cancelled) ensureQuestionData(question);
        }
      );
    };

    preloadCategoryWindow(currentPreloadCategory).then(() => {
      preloadCategoryInBackground(currentPreloadCategory);
      preloadCategoryInBackground(nextPreloadCategory);
    });

    return () => {
      cancelled = true;
    };
  }, [currentPreloadCategory, currentStep, ensureQuestionData, nextPreloadCategory, questions]);

  const getCachedQuestionData = useCallback(
    (question) => questionCache[getQuestionId(question)] || null,
    [questionCache]
  );

  const isCurrentCategoryReady = useMemo(
    () =>
      isCurrentQuestionReady(
        questions,
        currentStep,
        questionCache,
        preloadFailures
      ),
    [currentStep, preloadFailures, questionCache, questions]
  );

  return {
    getCachedQuestionData,
    currentWindowQuestions,
    isCurrentCategoryReady,
    questionCache,
  };
};
